import Image, { ImageLoader, ImageProps } from "next/image";
import { ImageSizes } from "../lib/utils/imageUtils";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { StoryblokAsset } from "../lib/storyblok/types/fieldtypes/storyblokAsset";

type CropData = {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
};

type ResizeData = {
  width?: number;
  height?: number;
};

type Props = {
  image: StoryblokAsset;
  sizes?: ImageSizes;
  layout?: string;
  contain?: boolean;
  width?: number;
  height?: number;
  crop?: CropData;
  resize?: ResizeData;
  priority?: boolean;
  keepProportions?: boolean;
};

type RemainingImageProps = Omit<
  ImageProps,
  "width" | "height" | "src" | "sizes"
>;

const getStoryblokImageSrcSize = (
  url: string
): { width: number; height: number } => {
  const match = url.match(/\/f\/\d+\/(\d+)x(\d+)\//);
  return match
    ? { width: parseInt(match[1], 10), height: parseInt(match[2], 10) }
    : { width: 0, height: 0 };
};

const getImageSize = (
  url: string,
  signal?: AbortSignal
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("Operation aborted"));
      return;
    }

    const img = new window.Image();

    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
      img.onabort = null;
    };

    const handleAbort = () => {
      cleanup();
      reject(new Error("Operation aborted"));
    };

    if (signal) {
      signal.addEventListener("abort", handleAbort);
    }

    img.onload = () => {
      cleanup();
      if (signal) {
        signal.removeEventListener("abort", handleAbort);
      }
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      cleanup();
      if (signal) {
        signal.removeEventListener("abort", handleAbort);
      }
      resolve({ width: 0, height: 0 });
    };

    img.onabort = () => {
      cleanup();
      if (signal) {
        signal.removeEventListener("abort", handleAbort);
      }
      reject(new Error("Image loading aborted"));
    };

    img.src = url;
  });
};

const getImageSizeFromCrop = (data?: CropData) => {
  if (
    !data ||
    typeof data.x1 !== "number" ||
    typeof data.x2 !== "number" ||
    typeof data.y1 !== "number" ||
    typeof data.y2 !== "number"
  ) {
    return { width: 0, height: 0 };
  }
  const width = Math.abs(data.x2 - data.x1);
  const height = Math.abs(data.y2 - data.y1);
  return { width, height };
};

const getImageFormat = (url: string): string => {
  if (!url) return "";
  const extension = url.split(".").pop()?.toLowerCase() || "";
  return extension.split(/[?#]/)[0];
};

const getOriginalStoryblokUrl = (url: string): string => {
  if (!url) return "";

  const match = url.match(/(https?:\/\/[^\/]+\/f\/[^\/]+\/[^\/]+\.[^\/\.]+)/);
  if (match && match[1]) {
    return match[1];
  }

  const parts = url.split(/\.(gif|jpg|jpeg|png|webp|avif)/i);
  if (parts.length > 1) {
    return parts[0] + "." + parts[1].split("/")[0];
  }

  return url;
};

const parseFocusToFocalPoint = (focus?: string): string => {
  if (!focus || typeof focus !== "string") {
    return "";
  }

  const match = focus.match(/^(\d+)x(\d+):(\d+)x(\d+)$/);
  if (!match) {
    return "";
  }

  const x1 = parseInt(match[1], 10);
  const y1 = parseInt(match[2], 10);
  const x2 = parseInt(match[3], 10);
  const y2 = parseInt(match[4], 10);

  if ([x1, y1, x2, y2].some((val) => isNaN(val))) {
    return "";
  }

  return `${x1}x${y1}:${x2}x${y2}`;
};

export const getStoryblokCropURI = (data?: CropData) => {
  if (
    !data ||
    typeof data.x1 !== "number" ||
    typeof data.x2 !== "number" ||
    typeof data.y1 !== "number" ||
    typeof data.y2 !== "number"
  ) {
    return "/m/0x0:0x0";
  }
  return `/m/${data.x1}x${data.y1}:${data.x2}x${data.y2}`;
};

export const storyblokLoader = ({
  src,
  width,
  quality,
  format,
  resize,
  focus,
}: {
  src: string;
  width: number;
  quality?: number;
  format?: string;
  resize?: ResizeData;
  focus?: string;
}): string => {
  const detectedFormat = format || getImageFormat(src);

  if (detectedFormat === "gif") {
    const match = src.match(/(https?:\/\/[^\/]+\/f\/[^\/]+\/[^\/]+\.[^\/\.]+)/);
    if (match && match[1]) {
      return match[1];
    }

    const parts = src.split(/\.(gif|jpg|jpeg|png|webp|avif)/i);
    if (parts.length > 1) {
      return parts[0] + "." + parts[1].split("/")[0];
    }

    return src;
  }

  let transformedUrl = src;

  let finalWidth = width;
  let finalHeight = 0;

  if (resize?.width && width > resize.width) {
    finalWidth = resize.width;
  }

  if (resize?.width && resize?.height) {
    finalWidth = resize.width;
    finalHeight = resize.height;
  }

  transformedUrl += `/${finalWidth}x${finalHeight}`;

  let filters = `filters:format(webp):quality(${quality || 75})`;

  if (focus) {
    filters = `filters:focal(${focus}):format(webp):quality(${quality || 75})`;
  }

  transformedUrl += `/${filters}`;

  return transformedUrl;
};

const StoryblokImage = ({
  image,
  layout,
  sizes,
  contain,
  crop,
  resize,
  priority = false,
  keepProportions = false,
  ...imageProps
}: RemainingImageProps & Props) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const imageFormat = useMemo(() => {
    return getImageFormat(image?.filename || "");
  }, [image?.filename]);

  const isGif = imageFormat === "gif";

  const cropData = useMemo(() => {
    return crop || image?.meta_data?.crop;
  }, [crop, image?.meta_data?.crop]);

  const focalPoint = useMemo(() => {
    return parseFocusToFocalPoint(image?.focus);
  }, [image?.focus]);

  const src = useMemo(() => {
    if (!image || !image.filename) {
      return "";
    }

    if (isGif) {
      return getOriginalStoryblokUrl(image.filename);
    }

    const cropURI = getStoryblokCropURI(cropData);
    return image.filename + cropURI;
  }, [image, cropData, isGif]);

  const cleanGifUrl = useMemo(() => {
    if (!isGif || !image?.filename) return "";
    return getOriginalStoryblokUrl(image.filename);
  }, [isGif, image?.filename]);

  // Calculate initial dimensions synchronously when possible
  const getInitialImageSize = useCallback(() => {
    if (!image?.filename) {
      return { width: 0, height: 0 };
    }

    // Try to get dimensions from crop data first
    if (
      cropData &&
      cropData.x1 !== undefined &&
      cropData.x2 !== undefined &&
      cropData.y1 !== undefined &&
      cropData.y2 !== undefined
    ) {
      const cropSize = getImageSizeFromCrop(cropData);
      if (cropSize.width && cropSize.height) {
        return cropSize;
      }
    }

    const urlDimensions = getStoryblokImageSrcSize(image.filename);
    if (urlDimensions.width && urlDimensions.height) {
      return urlDimensions;
    }

    return { width: 0, height: 0 };
  }, [image?.filename, cropData]);

  const effectiveLayout = useMemo(() => {
    if (keepProportions && layout === "fill") {
      return "responsive";
    }
    return layout;
  }, [keepProportions, layout]);

  const altText = image?.alt || "";

  const loader: ImageLoader = useCallback(
    ({ src, width, quality }) => {
      if (isGif) {
        return cleanGifUrl;
      }
      return storyblokLoader({
        src,
        width,
        quality,
        format: imageFormat,
        resize,
        focus: focalPoint,
      });
    },
    [imageFormat, isGif, cleanGifUrl, resize, focalPoint]
  );

  const [imageSize, setImageSize] = useState<{ width: number; height: number }>(
    getInitialImageSize
  );

  const [isLoadingSize, setIsLoadingSize] = useState(() => {
    const initial = getInitialImageSize();
    return effectiveLayout !== "fill" && (!initial.width || !initial.height);
  });

  useEffect(() => {
    if (typeof window === "undefined" || effectiveLayout === "fill" || !image) {
      setIsLoadingSize(false);
      return;
    }

    const initialSize = getInitialImageSize();
    if (initialSize.width && initialSize.height) {
      setIsLoadingSize(false);
      return;
    }

    setIsLoadingSize(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const loadImageSize = async () => {
      try {
        let size: { width: number; height: number };

        if (isGif) {
          size = await getImageSize(cleanGifUrl, signal);
        } else {
          size = await getImageSize(image.filename, signal);
        }

        if (!signal.aborted) {
          setImageSize(size);
          setIsLoadingSize(false);
        }
      } catch (error) {
        if (error instanceof Error && error.message !== "Operation aborted") {
          //console.warn("Failed to load image size:", error);
        }
        if (!signal.aborted) {
          setIsLoadingSize(false);
        }
      }
    };

    loadImageSize();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    image,
    effectiveLayout,
    cropData,
    isGif,
    cleanGifUrl,
    getInitialImageSize,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (!image || !image.filename) {
    return null;
  }

  const hasValidDimensions =
    effectiveLayout === "fill" || (imageSize.width > 0 && imageSize.height > 0);

  if (!hasValidDimensions && !isLoadingSize) {
    return null;
  }

  const unoptimized = isGif;

  return (
    <Image
      lazyBoundary="600px"
      loader={loader}
      alt={altText}
      src={src}
      layout={effectiveLayout}
      objectFit={contain ? "contain" : "cover"}
      width={effectiveLayout !== "fill" ? imageSize.width : undefined}
      height={effectiveLayout !== "fill" ? imageSize.height : undefined}
      sizes={
        effectiveLayout === "fill" || effectiveLayout === "responsive"
          ? sizes
          : undefined
      }
      quality={isGif ? undefined : imageProps.quality || 80}
      priority={priority}
      unoptimized={unoptimized}
      {...imageProps}
    />
  );
};

export default StoryblokImage;
